import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { Row, Col, Card, Typography, Tooltip, Button } from 'antd';
import { LockOutlined, CrownOutlined } from '@ant-design/icons';
import { IBrandEquityScoreResponse } from '../../services/nestjs-base-api';
import { useGetSystemSettingQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getDisplayName } from '../../utils/elementDisplayNames';

const { Text, Title } = Typography;

interface CardsVisualizationProps {
  results: IBrandEquityScoreResponse;
  onElementClick?: (elementName: string, elementData?: any) => void;
  focusedElementName?: string | null;
  isModal?: boolean;
}

export const CardsVisualization: React.FC<CardsVisualizationProps> = ({
  results,
  onElementClick,
  focusedElementName = null,
  isModal = false
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Create refs for each card to enable scrolling
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Scroll to focused element when it changes
  useEffect(() => {
    if (focusedElementName && cardRefs.current[focusedElementName]) {
      const cardElement = cardRefs.current[focusedElementName];
      if (cardElement) {
        cardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [focusedElementName]);

  // Fetch element descriptions from system settings
  const { data: elementDescriptionsData } = useGetSystemSettingQuery({
    jwtToken: user?.jwtToken || '',
    settingName: 'ELEMENT_DESCRIPTIONS'
  }, {
    skip: !user?.jwtToken
  });

  // Parse element descriptions
  const elementDescriptions = useMemo(() => {
    try {
      if (elementDescriptionsData) {
        let parsed;

        // Handle different response formats
        if (typeof elementDescriptionsData === 'string') {
          // Try to parse the string
          try {
            parsed = JSON.parse(elementDescriptionsData);

            // Check if the result is still a string (double-encoded JSON)
            if (typeof parsed === 'string') {
              parsed = JSON.parse(parsed);
            }
          } catch (parseError) {
            console.error('Failed to parse elementDescriptionsData string:', parseError);
            return {};
          }
        } else if (typeof elementDescriptionsData === 'object') {
          parsed = elementDescriptionsData;
        } else {
          return {};
        }

        // Handle different possible structures
        if (parsed && typeof parsed === 'object') {
          // If it has an 'elements' property, use that
          if (parsed.elements && typeof parsed.elements === 'object') {
            return parsed.elements;
          }
          // If it has a 'value' property (API might wrap the data), try to parse that
          else if (parsed.value) {
            try {
              const valueData = typeof parsed.value === 'string' ? JSON.parse(parsed.value) : parsed.value;
              return valueData.elements || {};
            } catch (e) {
              console.error('Failed to parse value property:', e);
            }
          }
          // If the parsed object itself looks like the elements structure, use it directly
          else if (Object.keys(parsed).length > 0 && !parsed.elements) {
            return parsed;
          }
        }
      }
    } catch (error) {
      console.error('Error parsing element descriptions:', error);
    }
    return {};
  }, [elementDescriptionsData]);
  // Get element color based on orbital visualization logic
  const getElementColor = useCallback((elementName: string): string => {
    // Brand Equity
    if (elementName === 'Brand Equity') {
      return '#fbbf24'; // golden/amber
    }

    // Check if latent element
    const isLatent = results.latentElementScores.some(le => le.element === elementName);

    if (isLatent) {
      // Specific latent element colors matching orbital visualization
      if (elementName === 'Brand Loyalty') {
        return '#10b981'; // green
      } else if (elementName === 'Attitudinal Loyalty' ||
                 elementName === 'Perceived Quality' ||
                 elementName === 'Behavioural Loyalty') {
        return '#fb923c'; // orange
      } else {
        return '#ef4444'; // red (Associations, Awareness, Brand Attachment, etc.)
      }
    } else {
      // Question Categories / Measured Elements
      return '#3b82f6'; // blue
    }
  }, [results.latentElementScores]);

  // Helper function to get card styling based on element color
  const getCardStyle = (color: string) => {
    // Convert hex to rgba for background
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    // Create darker version (70% of original, matching orbital visualization)
    const rDark = Math.round(r * 0.7);
    const gDark = Math.round(g * 0.7);
    const bDark = Math.round(b * 0.7);

    return {
      // Gradient from lighter at top-left to darker at bottom-right, matching orbital's radial gradient effect
      bg: `linear-gradient(135deg, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${rDark}, ${gDark}, ${bDark}, 0.35) 100%)`,
      border: color,
      accent: color,
      text: '#1f2937'
    };
  };

  // Get description from element (from ELEMENT_DESCRIPTIONS system setting)
  const getElementDescription = (elementName: string) => {
    // Check if we have a mouseover description for this element
    const elementDesc = elementDescriptions[elementName];
    if (elementDesc && elementDesc.mouseover) {
      return elementDesc.mouseover;
    }
    // Return empty string if no description available
    return '';
  };

  // Handle upgrade click for locked elements
  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
  };

  // Render a single card
  const renderCard = (name: string, score: number, elementData?: any, isLarge: boolean = false) => {
    const color = getElementColor(name);
    const colors = getCardStyle(color);
    const description = getElementDescription(name);
    const isLocked = elementData?.locked === true;

    // For locked elements, show grey/muted styling
    const lockedColors = isLocked ? {
      bg: 'linear-gradient(135deg, rgba(156, 163, 175, 0.15) 0%, rgba(107, 114, 128, 0.25) 100%)',
      border: '#9ca3af',
      accent: '#6b7280',
      text: '#9ca3af'
    } : colors;

    const cardContent = (
      <div ref={(el) => { cardRefs.current[name] = el; }}>
        <Card
          hoverable={!isLocked}
          onClick={() => !isLocked && onElementClick?.(name, elementData)}
          style={{
            height: isLarge ? '280px' : '220px',
            background: lockedColors.bg,
            borderRadius: '16px',
            border: `2px solid ${lockedColors.border}`,
            boxShadow: isLocked
              ? '0 2px 8px -2px rgba(0, 0, 0, 0.08)'
              : '0 4px 20px -4px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            position: 'relative',
            transition: 'all 0.2s ease',
            cursor: isLocked ? 'not-allowed' : 'pointer',
            opacity: isLocked ? 0.85 : 1
          }}
        >
          {/* Content Container */}
          <div style={{
            padding: isLarge ? '32px 24px' : '24px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative'
          }}>
            {/* Element Name - Always visible, not blurred */}
            <div style={{
              marginBottom: '16px',
              flex: '0 0 auto',
              textAlign: 'center'
            }}>
              <Text style={{
                fontSize: isLarge ? '20px' : '15px',
                fontWeight: 600,
                color: isLocked ? '#374151' : lockedColors.text,
                lineHeight: '1.3',
                display: 'block'
              }}>
                {getDisplayName(name)}
              </Text>
            </div>

            {/* Locked Content - Show upgrade UI */}
            {isLocked ? (
              <div style={{
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <LockOutlined style={{
                  fontSize: isLarge ? '40px' : '28px',
                  color: '#9ca3af',
                  marginBottom: '12px'
                }} />
                <Text style={{
                  color: '#6b7280',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '16px'
                }}>
                  Premium Feature
                </Text>
                <Button
                  type="primary"
                  size="small"
                  icon={<CrownOutlined />}
                  onClick={handleUpgradeClick}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                    border: 'none',
                    borderRadius: '6px'
                  }}
                >
                  Upgrade to Unlock
                </Button>
              </div>
            ) : (
              <>
                {/* Score */}
                <div style={{
                  flex: '1 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center'
                }}>
                  <Text style={{
                    fontSize: isLarge ? '48px' : '36px',
                    fontWeight: 800,
                    color: lockedColors.accent,
                    lineHeight: '1',
                    marginBottom: '8px'
                  }}>
                    {score.toFixed(0)}%
                  </Text>
                </div>

                {/* Description - only show if we have a description */}
                {description && (
                  <div style={{
                    flex: '0 0 auto',
                    borderTop: `1px solid ${lockedColors.accent}`,
                    paddingTop: '12px',
                    opacity: 0.9
                  }}>
                    <Text style={{
                      color: lockedColors.text,
                      fontSize: '12px',
                      lineHeight: '1.4',
                      display: 'block'
                    }}>
                      {description}
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    );

    // Wrap locked cards in a tooltip
    if (isLocked) {
      return (
        <Tooltip
          title="Upgrade your subscription to access this element"
          placement="top"
        >
          {cardContent}
        </Tooltip>
      );
    }

    return cardContent;
  };

  return (
    <div style={{
      width: '100%',
      padding: isModal ? '24px' : '24px 0',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* Brand Equity Score - Large Card at Top */}
      <Row justify="center" style={{ marginBottom: '48px' }}>
        <Col xs={24} sm={16} md={12} lg={8}>
          {renderCard('Brand Equity', results.overallBrandEquityScore, undefined, true)}
        </Col>
      </Row>

      {/* Latent Elements Section */}
      <div style={{ marginBottom: '48px' }}>
        <Title level={4} style={{
          marginBottom: '24px',
          textAlign: 'center',
          color: '#374151',
          fontSize: '20px',
          fontWeight: 700,
          letterSpacing: '0.5px'
        }}>
          LATENT ELEMENTS
        </Title>
        <Row gutter={[24, 24]}>
          {results.latentElementScores
            .filter(element => element.element !== 'Brand Equity')
            .map((element, index) => (
              <Col
                xs={24}
                sm={12}
                md={12}
                lg={6}
                key={element.element}
              >
                {renderCard(element.element, element.score ?? 0, element)}
              </Col>
            ))}
        </Row>
      </div>

      {/* Question Categories (Measure Elements) Section */}
      <div>
        <Title level={4} style={{
          marginBottom: '24px',
          textAlign: 'center',
          color: '#374151',
          fontSize: '20px',
          fontWeight: 700,
          letterSpacing: '0.5px'
        }}>
          MEASURE ELEMENTS
        </Title>
        <Row gutter={[24, 24]}>
          {results.questionCategoryScores.map((category, index) => (
            <Col
              xs={24}
              sm={12}
              md={12}
              lg={6}
              key={category.category}
            >
              {renderCard(category.category, category.meanScore || 0, category)}
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};
